/**
 * §11 Schnapsbattle — der Herausforderungs-Flow.
 *
 * Drei Sichten, je nachdem, wer gerade auf sein Handy schaut: der Fordernde
 * (wartet), der Geforderte (nimmt an oder lehnt ab), beide im Duell (jeder
 * würfelt SELBST und sieht seine eigene Wurf-Animation). Die Augen fallen auf
 * dem Server; die Animation deckt sie nur auf. Am Ende dreht der Verlierer am
 * Schnapsrad.
 */
import { useEffect, useMemo, useState } from 'react'
import { api, ApiFehler } from '../api/client.js'
import type { Battle, LedgerPosten, Schnaps, Teilnehmer } from '../api/typen.js'
import { Amtsknopf, Fehlermeldung } from './Bausteine.js'
import { Gluecksrad, useRadSteht } from './Roulette.js'

const AUGEN = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'] as const

interface RouletteAntwort {
  gezogen: Schnaps
  kandidaten: Schnaps[]
  gezogenIndex: number
}

interface WurfAntwort {
  battle: Battle
  wurf: { memberId: number; wuerfel: number[]; summe: number }
}

export function Schnapsbattle({
  eventId,
  battles,
  teilnehmer,
  ticker,
  eigeneMemberId,
  neuLaden,
}: {
  eventId: number
  battles: Battle[]
  teilnehmer: Teilnehmer[]
  ticker: LedgerPosten[]
  eigeneMemberId: number | undefined
  neuLaden: () => void
}) {
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [gegner, setGegner] = useState<number | ''>('')
  /** Lokale Wurf-Animation: solange sie läuft, rotieren die Augen. */
  const [wuerfelt, setWuerfelt] = useState(false)
  const [roulette, setRoulette] = useState<RouletteAntwort | null>(null)
  const radSteht = useRadSteht(roulette !== null)

  const reduziert = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  )

  const name = (memberId: number): string =>
    teilnehmer.find((t) => t.memberId === memberId)?.name ?? `Mitglied ${memberId}`
  const emoji = (memberId: number): string =>
    teilnehmer.find((t) => t.memberId === memberId)?.avatarEmoji ?? '🥃'

  const anwesende = teilnehmer.filter((t) => t.status === 'da')
  const aktives = battles.find(
    (b) =>
      (b.status === 'offen' || b.status === 'laeuft') &&
      (b.challengerMemberId === eigeneMemberId || b.opponentMemberId === eigeneMemberId),
  )
  const fremdes = battles.find((b) => b.status === 'offen' || b.status === 'laeuft')
  // Das jüngste entschiedene Battle, dessen Rad noch nicht gedreht ist.
  const entschieden = battles.find((b) => {
    if (b.status !== 'entschieden' || b.penaltyId === null) return false
    const posten = ticker.find((p) => p.id === b.penaltyId)
    return posten !== undefined && posten.schnapsId === null && posten.status === 'offen'
  })

  async function rufe<T>(pfad: string, body?: unknown): Promise<T | null> {
    setLaeuft(true)
    setFehler(null)
    try {
      return await api.post<T>(pfad, body)
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Der Antrag ist nicht durchgekommen.')
      return null
    } finally {
      setLaeuft(false)
    }
  }

  async function wirf(battleId: number): Promise<void> {
    setWuerfelt(true)
    const antwort = await rufe<WurfAntwort>(`/api/battles/${battleId}/wurf`)
    // Die Augen stehen längst fest — die Animation gönnt sich ihren Moment.
    const dauer = reduziert || antwort === null ? 0 : 900
    setTimeout(() => {
      setWuerfelt(false)
      neuLaden()
    }, dauer)
  }

  // -------------------------------------------------------------------------
  // Das Rad für den Verlierer
  // -------------------------------------------------------------------------
  if (roulette) {
    return (
      <div className="border border-eiche bg-bank px-4 py-5 text-center">
        <p className="protokoll">§11 · Das Rad entscheidet</p>
        <div className="mt-3">
          <Gluecksrad kandidaten={roulette.kandidaten} gezogenIndex={roulette.gezogenIndex} />
        </div>
        {radSteht && (
          <>
            <p className="mt-3 text-lampe">
              {roulette.gezogen.emoji} {roulette.gezogen.name}
            </p>
            <p className="protokoll mt-1 normal-case">Die Kammer wünscht wohl bekomms.</p>
            <div className="mt-3">
              <Amtsknopf
                onClick={() => {
                  setRoulette(null)
                  neuLaden()
                }}
              >
                Zur Kenntnis genommen
              </Amtsknopf>
            </div>
          </>
        )}
      </div>
    )
  }

  // -------------------------------------------------------------------------
  // Ein entschiedenes Battle wartet auf das Rad
  // -------------------------------------------------------------------------
  if (entschieden && entschieden.loserMemberId !== null) {
    const binVerlierer = entschieden.loserMemberId === eigeneMemberId
    return (
      <div className="border border-eiche bg-bank px-4 py-4 text-center">
        <p className="protokoll">§11 · Entschieden</p>
        <BattleRunden battle={entschieden} name={name} emoji={emoji} />
        <p className="mt-3 text-lampe">
          {emoji(entschieden.loserMemberId)} <strong>{name(entschieden.loserMemberId)}</strong> hat
          verloren.
        </p>
        {binVerlierer ? (
          <div className="mt-3">
            <p className="protokoll mb-2 normal-case">Das Urteil: einmal am Schnapsrad drehen.</p>
            <Amtsknopf
              variante="siegel"
              disabled={laeuft}
              onClick={() =>
                void rufe<RouletteAntwort>(`/api/penalties/${entschieden.penaltyId}/roulette`).then(
                  (a) => a && setRoulette(a),
                )
              }
            >
              Ans Rad
            </Amtsknopf>
          </div>
        ) : (
          <p className="protokoll mt-2 normal-case">
            {name(entschieden.loserMemberId)} muss noch am Rad drehen.
          </p>
        )}
        <Fehlermeldung text={fehler} />
      </div>
    )
  }

  // -------------------------------------------------------------------------
  // Mein aktives Battle
  // -------------------------------------------------------------------------
  if (aktives && eigeneMemberId !== undefined) {
    const binGefordert = aktives.opponentMemberId === eigeneMemberId
    const andere =
      aktives.challengerMemberId === eigeneMemberId
        ? aktives.opponentMemberId
        : aktives.challengerMemberId

    if (aktives.status === 'offen') {
      return (
        <div className="border border-messing/50 bg-bank px-4 py-4 text-center">
          <p className="protokoll">§11 · Herausforderung</p>
          {binGefordert ? (
            <>
              <p className="mt-2 text-lampe">
                {emoji(aktives.challengerMemberId)} {name(aktives.challengerMemberId)} fordert dich
                zum Schnapsbattle!
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Amtsknopf
                  disabled={laeuft}
                  onClick={() =>
                    void rufe(`/api/battles/${aktives.id}/annehmen`).then(() => neuLaden())
                  }
                >
                  Annehmen
                </Amtsknopf>
                <Amtsknopf
                  variante="leder"
                  disabled={laeuft}
                  onClick={() =>
                    void rufe(`/api/battles/${aktives.id}/ablehnen`).then(() => neuLaden())
                  }
                >
                  Ablehnen
                </Amtsknopf>
              </div>
            </>
          ) : (
            <>
              <p className="mt-2 text-sm text-akte/80">
                Deine Herausforderung an {emoji(andere)} {name(andere)} steht. Die Kammer wartet auf
                die Annahme.
              </p>
              <div className="mt-3">
                <Amtsknopf
                  variante="leder"
                  disabled={laeuft}
                  onClick={() =>
                    void rufe(`/api/battles/${aktives.id}/rueckzug`).then(() => neuLaden())
                  }
                >
                  Zurückziehen
                </Amtsknopf>
              </div>
            </>
          )}
          <Fehlermeldung text={fehler} />
        </div>
      )
    }

    // status === 'laeuft': würfeln!
    const aktuelleRunde = aktives.runden[aktives.runden.length - 1]
    const meinWurf = aktuelleRunde?.wuerfe.find((w) => w.memberId === eigeneMemberId)

    return (
      <div className="border border-messing/50 bg-bank px-4 py-4">
        <p className="protokoll text-center">
          §11 · {name(aktives.challengerMemberId)} ⚔ {name(aktives.opponentMemberId)}
        </p>
        <BattleRunden battle={aktives} name={name} emoji={emoji} />

        <div className="mt-4 text-center">
          {wuerfelt ? (
            <p aria-live="polite" className="text-3xl tracking-widest text-messing">
              <span className="wuerfel-rollen inline-block">⚂</span>{' '}
              <span className="wuerfel-rollen inline-block" style={{ animationDelay: '0.12s' }}>
                ⚄
              </span>
            </p>
          ) : meinWurf ? (
            <p className="text-sm text-akte/80">
              Dein Wurf steht: {meinWurf.wuerfel.map((w) => AUGEN[w - 1]).join(' ')} ={' '}
              {meinWurf.summe}. Warten auf {name(andere)} …
            </p>
          ) : (
            <Amtsknopf disabled={laeuft} onClick={() => void wirf(aktives.id)}>
              Würfeln
            </Amtsknopf>
          )}
        </div>
        <Fehlermeldung text={fehler} />
      </div>
    )
  }

  // -------------------------------------------------------------------------
  // Fremdes Battle läuft — zuschauen
  // -------------------------------------------------------------------------
  if (fremdes) {
    return (
      <div className="border border-eiche bg-bank px-4 py-4 text-center">
        <p className="protokoll">§11 · Battle im Gange</p>
        <p className="mt-2 text-sm text-akte/80">
          {emoji(fremdes.challengerMemberId)} {name(fremdes.challengerMemberId)} ⚔{' '}
          {emoji(fremdes.opponentMemberId)} {name(fremdes.opponentMemberId)}
          {fremdes.status === 'offen' ? ' — Herausforderung läuft.' : ' — es wird gewürfelt.'}
        </p>
        <BattleRunden battle={fremdes} name={name} emoji={emoji} />
      </div>
    )
  }

  // -------------------------------------------------------------------------
  // Aufstellung: jemanden fordern
  // -------------------------------------------------------------------------
  const gegnerKandidaten = anwesende.filter((t) => t.memberId !== eigeneMemberId)
  const binDa = anwesende.some((t) => t.memberId === eigeneMemberId)

  if (!binDa || gegnerKandidaten.length === 0) {
    return (
      <p className="border border-eiche bg-bank px-4 py-4 text-center text-sm text-akte/70">
        {binDa
          ? 'Niemand da, den du fordern könntest. Die Kammer wartet.'
          : 'Erst einchecken, dann fordern.'}
      </p>
    )
  }

  return (
    <div className="border border-eiche bg-bank px-4 py-4">
      <p className="text-sm text-akte/80">
        Wen forderst du? Der Gegner muss annehmen; dann würfelt jeder selbst. Wer verliert,
        schuldet eine Einheit und dreht am Schnapsrad.
      </p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {gegnerKandidaten.map((t) => (
          <button
            key={t.memberId}
            type="button"
            onClick={() => setGegner(t.memberId)}
            aria-pressed={gegner === t.memberId}
            className={`border px-3 py-1.5 text-sm transition-colors ${
              gegner === t.memberId
                ? 'border-messing bg-messing/15 text-lampe'
                : 'border-eiche text-akte/80 hover:border-messing/50'
            }`}
          >
            {t.avatarEmoji} {t.name}
          </button>
        ))}
      </div>
      <div className="mt-3">
        <Amtsknopf
          disabled={gegner === '' || laeuft}
          onClick={() =>
            void rufe(`/api/events/${eventId}/battles`, { gegnerMemberId: gegner }).then(() => {
              setGegner('')
              neuLaden()
            })
          }
        >
          Herausfordern
        </Amtsknopf>
      </div>
      <Fehlermeldung text={fehler} />
    </div>
  )
}

function BattleRunden({
  battle,
  name,
  emoji,
}: {
  battle: Battle
  name: (id: number) => string
  emoji: (id: number) => string
}) {
  if (battle.runden.length === 0) return null
  return (
    <div className="mt-3 flex flex-col gap-3 text-left">
      {battle.runden
        .filter((r) => r.wuerfe.length > 0)
        .map((runde) => (
          <div key={runde.rundeNr}>
            <p className="protokoll normal-case">
              Runde {runde.rundeNr}
              {runde.rundeNr > 1 && ' · Stechen'}
            </p>
            <div className="mt-1 flex flex-col gap-1">
              {runde.wuerfe.map((wurf) => (
                <div key={wurf.memberId} className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate text-sm text-lampe">
                    {emoji(wurf.memberId)} {name(wurf.memberId)}
                  </span>
                  <span className="shrink-0 font-akte text-lg tabular-nums text-akte">
                    <span aria-hidden="true" className="mr-2 text-2xl leading-none text-messing">
                      {wurf.wuerfel.map((w) => AUGEN[w - 1]).join(' ')}
                    </span>
                    = {wurf.summe}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  )
}
